import { Grid } from '../simulation/grid.ts';

interface Camera {
  x: number;
  y: number;
  zoom: number;
}

export class CanvasRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private camera: Camera = { x: 0, y: 0, zoom: 1 };
  private cellSize = 8;

  private isPanning = false;
  private lastMouse = { x: 0, y: 0 };

  private isDrawing = false;
  private drawValue = true;

  private currentGrid: Grid | null = null;

  onCellToggle: ((x: number, y: number, value: boolean) => void) | null = null;

  private aliveColor = '#34495e';
  private deadColor = '#ffffff';
  private gridLineColor = '#b0b0b0';
  private gridBorderColor = '#808080';

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get 2d context');
    this.ctx = ctx;

    this.handleResize();
    window.addEventListener('resize', () => this.handleResize());

    this.canvas.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false });
    this.canvas.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    window.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    window.addEventListener('mouseup', () => this.handleMouseUp());
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  private handleResize(): void {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  }

  private handleWheel(e: WheelEvent): void {
    e.preventDefault();
    const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
    const newZoom = Math.max(0.1, Math.min(10, this.camera.zoom * zoomFactor));

    const rect = this.canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    const worldX = (mx - this.camera.x) / this.camera.zoom;
    const worldY = (my - this.camera.y) / this.camera.zoom;

    this.camera.zoom = newZoom;
    this.camera.x = mx - worldX * newZoom;
    this.camera.y = my - worldY * newZoom;
  }

  private handleMouseDown(e: MouseEvent): void {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      this.isPanning = true;
      this.lastMouse = { x: e.clientX, y: e.clientY };
      this.canvas.style.cursor = 'grabbing';
      return;
    }

    if (e.button === 0) {
      this.isDrawing = true;
      const cell = this.screenToCell(e.clientX, e.clientY);
      if (cell) {
        this.drawValue = !this.getCellAtScreen(e.clientX, e.clientY);
        this.onCellToggle?.(cell.x, cell.y, this.drawValue);
      }
    }
  }

  private handleMouseMove(e: MouseEvent): void {
    if (this.isPanning) {
      const dx = e.clientX - this.lastMouse.x;
      const dy = e.clientY - this.lastMouse.y;
      this.camera.x += dx;
      this.camera.y += dy;
      this.lastMouse = { x: e.clientX, y: e.clientY };
      return;
    }

    if (this.isDrawing) {
      const cell = this.screenToCell(e.clientX, e.clientY);
      if (cell) {
        this.onCellToggle?.(cell.x, cell.y, this.drawValue);
      }
    }
  }

  private handleMouseUp(): void {
    this.isPanning = false;
    this.isDrawing = false;
    this.canvas.style.cursor = 'default';
  }

  private screenToCell(sx: number, sy: number): { x: number; y: number } | null {
    const rect = this.canvas.getBoundingClientRect();
    const mx = sx - rect.left;
    const my = sy - rect.top;

    const worldX = (mx - this.camera.x) / this.camera.zoom;
    const worldY = (my - this.camera.y) / this.camera.zoom;

    const cellX = Math.floor(worldX / this.cellSize);
    const cellY = Math.floor(worldY / this.cellSize);

    return { x: cellX, y: cellY };
  }

  private getCellAtScreen(sx: number, sy: number): boolean {
    if (!this.currentGrid) return false;

    const cell = this.screenToCell(sx, sy);
    if (!cell) return false;

    return this.currentGrid.get(cell.x, cell.y);
  }

  centerOn(gridWidth: number, gridHeight: number): void {
    const totalW = gridWidth * this.cellSize;
    const totalH = gridHeight * this.cellSize;
    this.camera.zoom = Math.min(
      this.canvas.width / totalW,
      this.canvas.height / totalH
    ) * 0.85;
    this.camera.x = (this.canvas.width - totalW * this.camera.zoom) / 2;
    this.camera.y = (this.canvas.height - totalH * this.camera.zoom) / 2;
  }

  render(grid: Grid): void {
    this.currentGrid = grid;

    const { ctx, canvas, camera } = this;
    const cs = this.cellSize;

    ctx.fillStyle = this.deadColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(camera.x, camera.y);
    ctx.scale(camera.zoom, camera.zoom);

    const gridW = grid.width * cs;
    const gridH = grid.height * cs;
    ctx.fillStyle = this.deadColor;
    ctx.fillRect(0, 0, gridW, gridH);

    // Determine visible bounds for culling
    const invZoom = 1 / camera.zoom;
    const viewLeft = Math.max(0, Math.floor(-camera.x * invZoom / cs));
    const viewTop = Math.max(0, Math.floor(-camera.y * invZoom / cs));
    const viewRight = Math.min(grid.width, Math.ceil((-camera.x + canvas.width) * invZoom / cs));
    const viewBottom = Math.min(grid.height, Math.ceil((-camera.y + canvas.height) * invZoom / cs));

    // Draw alive cells
    ctx.fillStyle = this.aliveColor;
    for (let y = viewTop; y < viewBottom; y++) {
      for (let x = viewLeft; x < viewRight; x++) {
        if (grid.get(x, y)) {
          ctx.fillRect(x * cs + 0.5, y * cs + 0.5, cs - 1, cs - 1);
        }
      }
    }

    // Draw grid lines only when zoomed in enough
    if (camera.zoom > 0.5) {
      ctx.strokeStyle = this.gridLineColor;
      ctx.lineWidth = 0.5;
      ctx.beginPath();

      for (let x = viewLeft; x <= viewRight; x++) {
        ctx.moveTo(x * cs, viewTop * cs);
        ctx.lineTo(x * cs, viewBottom * cs);
      }
      for (let y = viewTop; y <= viewBottom; y++) {
        ctx.moveTo(viewLeft * cs, y * cs);
        ctx.lineTo(viewRight * cs, y * cs);
      }

      ctx.stroke();
    }

    // Grid border
    ctx.strokeStyle = this.gridBorderColor;
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, gridW, gridH);

    ctx.restore();
  }
}
